import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import { InternalNotes } from "../../types/InternalNotes";
import { isEqual } from "lodash";
import api from "../../utils/api";

interface NotesState {
  notes: InternalNotes[];
  isLoading: boolean;
  error: string | null;
}

const initialState: NotesState = {
  notes: [],
  isLoading: false,
  error: null,
};

export const addInternalNote = createAsyncThunk(
  "internalNotes/addNotes",
  async (
    {
      case_id,
      author,
      note,
    }: { case_id: string; author: string; note: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/auth/admin/note/add`,
        {
          case_id,
          author,
          note,
        }
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

export const fetchInternalNote = createAsyncThunk(
  "internalNotes/fetchNotes",
  async (case_id: string, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/auth/admin/note/${case_id}`
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message);
    }
  }
);

const internalNotesSlice = createSlice({
  name: "adminInternalNotes",
  initialState,
  reducers: {
    clearNotes: (state) => {
      state.notes = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addInternalNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addInternalNote.fulfilled, (state, action) => {
        state.isLoading = false;
        if (Array.isArray(action.payload)) {
          state.notes = action.payload;
        } else if (
          typeof action.payload === "object" &&
          action.payload !== null
        ) {
          state.notes.push(action.payload);
        } else {
          state.notes = [];
        }
      })
      .addCase(addInternalNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchInternalNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchInternalNote.fulfilled, (state, action) => {
        state.isLoading = false;

        const newNotes = Array.isArray(action.payload) ? action.payload : [];

        // Unwrap Immer proxy before lodash isEqual (proxy breaks prototype access)
        if (!isEqual(current(state.notes), newNotes)) {
          state.notes = newNotes;
        }
      })
      .addCase(fetchInternalNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearNotes } = internalNotesSlice.actions;
export default internalNotesSlice.reducer;
